import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight, BarChart3, Phone, Mail, Users, Target, Zap, Brain, MessageSquare, TrendingUp, Workflow, Globe, Shield, Clock, CheckCircle2, Linkedin, Twitter, Youtube, Menu, X, ChevronUp } from "lucide-react";
import aiveliqLogo from "@/assets/aiveliq360-logo.png";
import { DemoVideoDialog } from "@/components/demo/DemoVideoDialog";
import { HeroStats } from "@/components/landing/HeroStats";

import { Testimonials } from "@/components/landing/Testimonials";
import { PricingCards } from "@/components/landing/PricingCards";
import { FAQ } from "@/components/landing/FAQ";
import { SEOHead } from "@/components/seo/SEOHead";
const featurePillars = [{
  icon: Target,
  title: "AI Lead Sourcing Engine",
  description: "Automatically discovers prospects based on ICP, buying intent, and real-time behavioral signals."
}, {
  icon: Mail,
  title: "AI Sales Engagement Workflows",
  description: "Multi-channel outreach, personalized messaging, and intelligent follow-ups executed autonomously."
}, {
  icon: MessageSquare,
  title: "AI Coaching & Objection Handling",
  description: "Real-time scripts, tailored prompts, rebuttals, and dynamic talk tracks for SDRs and AEs."
}, {
  icon: BarChart3,
  title: "AI Revenue Intelligence Dashboard",
  description: "Forecasts deal velocity, pipeline health, rep performance, and conversion metrics."
}, {
  icon: Brain,
  title: "AI Playbooks & Automation",
  description: "Auto-builds sales playbooks using historical wins, industry benchmarks, and buyer analytics."
}, {
  icon: Workflow,
  title: "GTM Workflow Automation",
  description: "Align marketing, sales, and customer success with one cohesive AI-powered operating workflow."
}];
const howItWorksSteps = [{
  step: "01",
  title: "Identify",
  description: "Aiveliq360 finds and prioritizes leads using AI-driven ICP models, intent scoring, and cross-channel data enrichment.",
  icon: Target
}, {
  step: "02",
  title: "Engage",
  description: "AI crafts personalized outreach sequences across email, SMS, social, and voice — adjusting tone, timing, and messaging based on live responses.",
  icon: Mail
}, {
  step: "03",
  title: "Guide & Support",
  description: "Real-time coaching improves rep performance with dynamic playbooks, objection handling, and intelligent call scripting.",
  icon: MessageSquare
}, {
  step: "04",
  title: "Close",
  description: "AI accelerates deal cycles through predictive scoring, risk alerts, next-step recommendations, and automated follow-up workflows.",
  icon: TrendingUp
}, {
  step: "05",
  title: "Optimize",
  description: "Aiveliq360 continuously learns from your deals, win/loss data, customer behaviors, and rep performance.",
  icon: Brain
}];
const coreCapabilities = [{
  title: "AI-Driven Lead Generation",
  description: "Find high-intent leads instantly using AI models trained on your ICP, industry patterns, competitor behaviors, and buying signals.",
  features: ["Automated prospect discovery", "AI scoring for highest conversion potential", "Database enrichment and validation"],
  icon: Target
}, {
  title: "AI Sales Engagement Engine",
  description: "Engagement sequences crafted and optimized automatically.",
  features: ["Personalized email campaigns", "Automated follow-ups", "Multi-channel outreach", "Buyer-behavior driven message variation"],
  icon: Mail
}, {
  title: "AI Objection Handling & Coaching",
  description: "Turn every rep into a top performer.",
  features: ["Dynamic scripts", "Real-time responses", "AI-generated talk tracks", "Objection libraries tailored to your product"],
  icon: MessageSquare
}, {
  title: "Revenue Intelligence & Forecasting",
  description: "Forecast with precision.",
  features: ["Deal risk prediction", "Pipeline health scoring", "Conversion probability models", "Trend analysis and revenue projections"],
  icon: BarChart3
}, {
  title: "GTM Workflow Automation",
  description: "Unify marketing, sales, and customer success.",
  features: ["Automated task routing", "SLA management", "Escalations and notifications", "Cross-functional alignment"],
  icon: Workflow
}];
export default function Index() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return <div className="min-h-screen bg-background">
      <SEOHead title="Aiveliq360 | The 360° AI Sales Operating System for Revenue Teams" description="Aiveliq360 unifies lead generation, engagement, forecasting, coaching, and workflow automation into one intelligent AI Sales Operating System built for high-velocity growth." />

      {/* Sticky Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/50">
        <nav className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between" aria-label="Main navigation">
          <Link to="/">
            <img src={aiveliqLogo} alt="Aiveliq360 - AI Sales Operating System" className="h-10 object-contain" />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-2">
            <Link to="/app">
              <Button variant="ghost" size="sm">Dashboard</Button>
            </Link>
            <Link to="/pricing">
              <Button variant="ghost" size="sm">Pricing</Button>
            </Link>
            <Link to="/products/ai-sales-os">
              <Button variant="ghost" size="sm">Products</Button>
            </Link>
            <Link to="/integrations">
              <Button variant="ghost" size="sm">Integrations</Button>
            </Link>
            <Link to="/auth">
              <Button size="sm" className="gap-1.5 ai-glow">
                Get Started
                <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </nav>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border/50 bg-background/95 backdrop-blur-md">
            <div className="flex flex-col p-4 space-y-2">
              <Link to="/app" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" size="sm" className="w-full justify-start">Dashboard</Button>
              </Link>
              <Link to="/pricing" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" size="sm" className="w-full justify-start">Pricing</Button>
              </Link>
              <Link to="/products/ai-sales-os" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" size="sm" className="w-full justify-start">Products</Button>
              </Link>
              <Link to="/integrations" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" size="sm" className="w-full justify-start">Integrations</Button>
              </Link>
              <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                <Button size="sm" className="w-full gap-1.5 ai-glow">
                  Get Started
                  <ArrowRight className="h-3.5 w-3.5" />
                </Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-purple-500/5" />
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-6xl mx-auto px-4 py-12 lg:py-16">

          {/* Hero Content */}
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-medium text-primary">
                360° AI Sales Operating System
              </span>
            </div>

            <h1 className="text-3xl lg:text-5xl font-bold tracking-tight mb-4">
              The 360° AI Sales Operating System Built for{" "}
              <span className="gradient-text">High-Velocity Revenue Teams</span>
            </h1>

            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">Unify your entire revenue engine  lead generation, sales engagement, forecasting, coaching, analytics, and workflow automation  into one autonomous AI platform designed to deliver consistent, predictable growth.</p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
              <Link to="/auth">
                <Button size="lg" className="gap-1.5 ai-glow">
                  Start Free Trial
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
              <Button size="lg" variant="outline" onClick={() => setDemoOpen(true)}>
                Request a Demo
              </Button>
            </div>

            <DemoVideoDialog open={demoOpen} onOpenChange={setDemoOpen} />
            <HeroStats />
          </div>
        </div>
      </section>

      {/* Value Proposition Section */}
      <section className="max-w-6xl mx-auto px-4 py-16" aria-labelledby="value-prop-heading">
        <div className="text-center mb-12">
          <h2 id="value-prop-heading" className="text-2xl lg:text-3xl font-bold mb-3">
            The Only AI Platform That Runs Your{" "}
            <span className="gradient-text">Entire Sales Funnel End-to-End</span>
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">Aiveliq360 replaces fragmented sales tools with a single intelligent OS that sources leads, automates outreach, handles objections, guides your sellers, forecasts pipeline health, and accelerates every deal cycle  all in one powerful 360° platform.</p>
        </div>
      </section>

      {/* Feature Pillars */}
      <section className="max-w-6xl mx-auto px-4 py-12" aria-labelledby="features-heading">
        <div className="text-center mb-10">
          <h2 id="features-heading" className="text-2xl lg:text-3xl font-bold mb-2">
            Transform Your Sales Team Into an{" "}
            <span className="gradient-text">Autonomous, Always-On Revenue Engine</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
          {featurePillars.map(feature => <article key={feature.title} className="glass rounded-lg p-5 hover:border-primary/30 hover-lift transition-all duration-300 group">
              <div className="p-2 rounded-lg bg-primary/10 w-fit mb-3 group-hover:ai-glow-subtle transition-all duration-300">
                <feature.icon className="h-5 w-5 text-primary" />
              </div>
              <h3 className="text-base font-semibold mb-2">{feature.title}</h3>
              <p className="text-muted-foreground text-sm">{feature.description}</p>
            </article>)}
        </div>

        <div className="text-center mt-8">
          <Link to="/products/ai-sales-os">
            <Button variant="outline" className="gap-2">
              See Aiveliq360 in Action
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-muted/30 py-16" aria-labelledby="how-it-works-heading">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 id="how-it-works-heading" className="text-2xl lg:text-3xl font-bold mb-3">
              A Fully Integrated AI System That{" "}
              <span className="gradient-text">Learns, Optimizes, and Executes</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {howItWorksSteps.map((item, index) => <article key={item.step} className="relative">
                <div className="glass rounded-lg p-5 h-full">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl font-bold text-primary/40">{item.step}</span>
                    <item.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.description}</p>
                </div>
                {index < howItWorksSteps.length - 1 && <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2">
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                  </div>}
              </article>)}
          </div>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="max-w-6xl mx-auto px-4 py-16" aria-labelledby="capabilities-heading">
        <div className="text-center mb-12">
          <h2 id="capabilities-heading" className="text-2xl lg:text-3xl font-bold mb-3">
            A Complete 360° AI Platform for{" "}
            <span className="gradient-text">Modern Revenue Teams</span>
          </h2>
        </div>

        <div className="space-y-6">
          {coreCapabilities.map((capability, index) => <article key={capability.title} className="glass rounded-xl p-6 hover:border-primary/30 transition-all duration-300">
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="lg:w-1/3">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 rounded-lg bg-primary/10">
                      <capability.icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold">{capability.title}</h3>
                  </div>
                  <p className="text-muted-foreground text-sm">{capability.description}</p>
                </div>
                <div className="lg:w-2/3">
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {capability.features.map(feature => <li key={feature} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                        <span>{feature}</span>
                      </li>)}
                  </ul>
                </div>
              </div>
            </article>)}
        </div>

        <div className="text-center mt-8">
          <Link to="/products/ai-sales-os">
            <Button className="gap-2 ai-glow">
              Explore Full Product Suite
              <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>


      {/* Testimonials */}
      <Testimonials />

      {/* Pricing */}
      <PricingCards />

      {/* Comparison Links */}
      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-8">
          <h2 className="text-xl font-bold mb-2">See How We Compare</h2>
          <p className="text-muted-foreground text-sm">
            Discover why teams switch to Aiveliq360
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/compare/vs-gong">
            <Button variant="outline" size="sm">Aiveliq360 vs Gong</Button>
          </Link>
          <Link to="/compare/vs-outreach">
            <Button variant="outline" size="sm">Aiveliq360 vs Outreach</Button>
          </Link>
          <Link to="/compare/vs-apollo">
            <Button variant="outline" size="sm">Aiveliq360 vs Apollo</Button>
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* Final CTA Section */}
      <section className="max-w-6xl mx-auto px-4 py-16" aria-labelledby="cta-heading">
        <div className="glass rounded-xl p-10 text-center gradient-border">
          <h2 id="cta-heading" className="text-2xl lg:text-3xl font-bold mb-3">
            Upgrade Your Sales Team With a{" "}
            <span className="gradient-text">360° AI Revenue Engine</span>
          </h2>
          <p className="text-muted-foreground max-w-lg mx-auto mb-8">
            Join high-velocity revenue teams using Aiveliq360 to achieve consistent, 
            predictable growth with autonomous AI-powered sales operations.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/auth">
              <Button size="lg" className="gap-1.5 ai-glow">
                Start Free Trial
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
            <Button size="lg" variant="outline" onClick={() => setDemoOpen(true)}>
              Book a Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <img src={aiveliqLogo} alt="Aiveliq360" className="h-6 object-contain" />
              <div className="flex items-center gap-2">
                <a href="https://linkedin.com/company/aiveliq360" target="_blank" rel="noopener noreferrer" className="p-2 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" aria-label="LinkedIn">
                  <Linkedin className="h-4 w-4" />
                </a>
                <a href="https://twitter.com/aiveliq360" target="_blank" rel="noopener noreferrer" className="p-2 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" aria-label="Twitter">
                  <Twitter className="h-4 w-4" />
                </a>
                <a href="https://youtube.com/@aiveliq360" target="_blank" rel="noopener noreferrer" className="p-2 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" aria-label="YouTube">
                  <Youtube className="h-4 w-4" />
                </a>
              </div>
            </div>
            <nav className="flex flex-wrap items-center justify-center gap-4 text-xs text-muted-foreground" aria-label="Footer navigation">
              <Link to="/pricing" className="hover:text-foreground transition-colors">Pricing</Link>
              <Link to="/products/ai-sales-os" className="hover:text-foreground transition-colors">Products</Link>
              <Link to="/integrations" className="hover:text-foreground transition-colors">Integrations</Link>
              <Link to="/compare/vs-gong" className="hover:text-foreground transition-colors">Compare</Link>
              <Link to="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link to="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
            </nav>
            <div className="flex flex-col items-center md:items-end gap-1 text-xs text-muted-foreground">
              <p>© 2025 Aiveliq360. All rights reserved.</p>
              <p>Powered by <span className="font-medium text-foreground">Stratlance</span></p>
            </div>
          </div>
        </div>
      </footer>

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Aiveliq360",
        "url": "https://aiveliq360.com",
        "logo": "https://aiveliq360.com/og-image.png",
        "description": "The 360° AI Sales Operating System built for high-velocity revenue teams.",
        "sameAs": ["https://twitter.com/Aiveliq360"]
      })
    }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Aiveliq360",
        "applicationCategory": "BusinessApplication",
        "operatingSystem": "Web",
        "description": "AI Sales Operating System that unifies lead generation, sales engagement, forecasting, coaching, and workflow automation.",
        "offers": {
          "@type": "AggregateOffer",
          "priceCurrency": "USD",
          "lowPrice": "99",
          "highPrice": "2000"
        }
      })
    }} />

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 z-50 p-3 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all duration-300 ai-glow"
          aria-label="Back to top"
        >
          <ChevronUp className="h-5 w-5" />
        </button>
      )}
    </div>;
}